import { useContext, useState, useCallback, useMemo } from 'react'
import { Redirect, useParams, Link } from 'react-router-dom'
import { useQuery } from 'react-query'
import moment from 'moment'
import { Alert, Form } from 'antd'
import { useTranslation } from 'react-i18next'

import { AppContext } from '../../context/AppContext'

import PageWrap from '../../components/layout/PageWrap'
import FormWrap from '../../components/forms/FormWrap'
import FormBlock from '../../components/forms/FormBlock'
import Loader from '../../components/Loader'
import Error from '../../components/Error'
import SplitGiftCardSection from '../../components/giftCards/SplitGiftCardSection'

import { GiftCardEdit, cleanGiftCardPayload } from '../../constants/forms/GiftCard'
import { EditGiftCard, getSingleGiftCard } from '../../network/GiftCards'
import {
    attachGiftCardPhoneBlurToTemplate,
    promptLinkCustomerByPhone,
    shouldLookupPhone,
} from '../../utils/giftCardPhoneLookup'

const formatData = (data) => ({
    giftCardID: data.giftCardID,
    grantedByName: data.grantedBy?.name,
    grantedBy: data.grantedBy?.id,
    type: data.type,
    customer: data.customer?.id
        ? {
              value: data.customer.id,
              label: data.customer.name || data.customer.id,
          }
        : undefined,
    offeredBy: data.offeredBy?.id
        ? {
              value: data.offeredBy.id,
              label: data.offeredBy.name || data.offeredBy.id,
          }
        : undefined,
    phoneNumber: data.customer ? undefined : data.phoneNumber,
    amount: data.amount,
    expirationDate: data.expirationDate
        ? moment(data.expirationDate)
        : undefined,
    communities: data.communities?.map((c) => c.id),
    status: data.status,
    parentGiftCardID: data.parent?.giftCardID || data.parent?.id,
})

const EditGiftCardPage = () => {
    const { token } = useContext(AppContext)
    const { giftCardId } = useParams()
    const [form] = Form.useForm()
    const { t } = useTranslation()
    const [dismissedPhoneLookup, setDismissedPhoneLookup] = useState(null)

    const giftCardDetails = useQuery(
        [
            'getSingleGiftCard',
            {
                giftCardId,
                token: token.token,
            },
        ],
        getSingleGiftCard,
        {
            refetchOnWindowFocus: false,
        }
    )

    const handlePhoneBlur = useCallback(
        async (formInstance, name, value) => {
            const values = formInstance.getFieldsValue()
            if (
                !shouldLookupPhone(
                    { ...values, phoneNumber: value },
                    dismissedPhoneLookup
                )
            ) {
                return
            }

            await promptLinkCustomerByPhone({
                phone: value,
                token: token.token,
                form: formInstance,
                t,
                dismissedPhone: dismissedPhoneLookup,
                setDismissedPhone: setDismissedPhoneLookup,
            })
        },
        [dismissedPhoneLookup, token.token, t]
    )

    const beforeSubmit = useCallback(
        async (values) => {
            if (shouldLookupPhone(values, dismissedPhoneLookup)) {
                await promptLinkCustomerByPhone({
                    phone: values.phoneNumber,
                    token: token.token,
                    form,
                    t,
                    dismissedPhone: dismissedPhoneLookup,
                    setDismissedPhone: setDismissedPhoneLookup,
                })
            }

            return true
        },
        [dismissedPhoneLookup, token.token, form, t]
    )

    const formTemplate = useMemo(
        () =>
            attachGiftCardPhoneBlurToTemplate(GiftCardEdit, handlePhoneBlur),
        [handlePhoneBlur]
    )

    const giftCard = giftCardDetails.data
    const parentId = giftCard?.parent?.id || giftCard?.parent?._id
    const canSplit = giftCard?.status === 'validated'

    if (
        giftCard?.status === 'used'
        || giftCard?.status === 'split'
        || giftCard?.status === 'expired'
    ) {
        return <Redirect to="/gift-cards" />
    }

    return (
        <PageWrap
            goBack
            isLoading={giftCardDetails.isLoading}
            title={giftCard?.giftCardID || 'edit_gift_card'}
        >
            {giftCardDetails.isError ? (
                <Error retry={() => giftCardDetails.refetch()} />
            ) : giftCardDetails.isLoading ? (
                <Loader />
            ) : (
                <FormWrap
                    title="edit_gift_card"
                    form={form}
                    request={EditGiftCard}
                    id={giftCardId}
                    beforeSubmit={beforeSubmit}
                    onSuccess="/gift-cards"
                    onCancel="/gift-cards"
                    data={formatData(giftCard)}
                    cleanBeforeSending={cleanGiftCardPayload}
                    invalidate={[
                        'getGiftCards',
                        'getSingleGiftCard',
                        'getCustomerGiftCards',
                    ]}
                >
                    <div className="SectionVerticalWrap">
                        {parentId && (
                            <Alert
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                                message={t('GIFT_CARDS_PARENT_LABEL')}
                                description={
                                    giftCard.parent?.status === 'split' ? (
                                        giftCard.parent?.giftCardID || parentId
                                    ) : (
                                        <Link to={`/gift-cards/${parentId}`}>
                                            {giftCard.parent?.giftCardID || parentId}
                                        </Link>
                                    )
                                }
                            />
                        )}
                        <FormBlock
                            formTemplate={formTemplate}
                            form={form}
                        />
                        {canSplit && (
                            <SplitGiftCardSection giftCard={giftCard} />
                        )}
                    </div>
                </FormWrap>
            )}
        </PageWrap>
    )
}

export default EditGiftCardPage
